import { Link } from 'react-router-dom';

export default function EnglishForAdultsPage() {
  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cefrLevels = [
    { level: 'A1+', book: 'Keynote Elementary', desc: 'Basic everyday English' },
    { level: 'A2+', book: 'Keynote Pre-Intermediate', desc: 'Simple conversations & opinions' },
    { level: 'B1+', book: 'Keynote Intermediate', desc: 'Confident daily communication' },
    { level: 'B2+', book: 'Keynote Upper Intermediate', desc: 'Complex discussions & debates' },
    { level: 'C1', book: 'Keynote Advanced', desc: 'Professional-level fluency' },
    { level: 'C2', book: 'Keynote Proficiency', desc: 'Near-native mastery' },
  ];

  const classFeatures = [
    {
      icon: 'ri-global-line',
      title: 'Globally Recognised System',
      description: 'We use the Keynote Series by National Geographic Learning, a structured and internationally trusted English programme aligned with the CEFR framework.',
    },
    {
      icon: 'ri-chat-voice-line',
      title: 'Build Communication Skills',
      description: 'Develop real-world speaking and listening abilities through guided conversations, pair work, and group discussions in every class.',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Real-World Ideas & TED-Style Discussions',
      description: 'Explore modern global topics inspired by short TED talks — practise critical thinking and expressing opinions clearly and confidently.',
    },
    {
      icon: 'ri-team-line',
      title: 'Interactive Group Class',
      description: 'Engage in real presentation practice, group discussions, and pair work & speaking tasks in a supportive learning environment.',
    },
  ];

  const careerSkills = [
    {
      icon: 'ri-presentation-line',
      title: 'Presentation Skills',
      description: 'Learn to present ideas clearly and confidently in front of an audience — essential for meetings, pitches, and professional settings.',
    },
    {
      icon: 'ri-mail-send-line',
      title: 'Professional Communication',
      description: 'Improve your ability to write emails, reports, and communicate effectively with colleagues and clients in English.',
    },
    {
      icon: 'ri-discuss-line',
      title: 'Negotiation & Persuasion',
      description: 'Build the language skills needed to negotiate, persuade, and express your viewpoint in business discussions.',
    },
    {
      icon: 'ri-user-star-line',
      title: 'Interview Confidence',
      description: 'Practise answering questions, describing your experience, and making a strong impression in job interviews.',
    },
    {
      icon: 'ri-global-line',
      title: 'Cross-Cultural Awareness',
      description: 'Understand cultural nuances in global communication — crucial for working in international teams and environments.',
    },
    {
      icon: 'ri-bar-chart-grouped-line',
      title: 'Leadership Communication',
      description: 'Develop the vocabulary and tone needed to lead meetings, give feedback, and inspire your team with clarity.',
    },
  ];

  const whoIsItFor = [
    'Working professionals who need English for career advancement',
    'Adults preparing for job interviews or workplace communication',
    'Business owners who interact with international clients',
    'Anyone looking to improve everyday English speaking confidence',
    'Adults returning to learning after a long break',
    'Parents who want to support their children\'s English learning journey',
  ];

  return (
    <main className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#2C5F2D] via-[#3A7D3C] to-[#4A9D4F] text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <div className="inline-block bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6">
            <span className="text-sm font-semibold">Ages 18+</span>
          </div>
          <h1 className="font-['Yeseva One'] text-4xl md:text-5xl mb-4">
            English for Adults
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl">
            A CEFR-aligned programme using the Keynote Series by National Geographic Learning — build real communication skills through global topics and interactive discussions.
          </p>
        </div>
      </section>

      {/* Why Our Class - Key Features */}
      <section className="py-16 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-['Yeseva One'] text-4xl text-[#153223] text-center mb-4">
            Why Our English Class for Adults?
          </h2>
          <p className="text-center text-gray-700 text-lg mb-12 max-w-3xl mx-auto">
            We combine internationally recognised materials with interactive teaching methods to help you communicate with confidence
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {classFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#153223] rounded-full flex-shrink-0">
                    <i className={`${feature.icon} text-2xl text-[#FFE66D]`}></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#153223] mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CEFR Framework Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-['Yeseva One'] text-4xl text-[#153223] text-center mb-4">
            CEFR-Aligned Learning Framework
          </h2>
          <p className="text-center text-gray-700 text-lg mb-12 max-w-3xl mx-auto">
            Our materials follow the <strong>Common European Framework of Reference for Languages (CEFR)</strong> — a globally recognised standard for measuring language proficiency
          </p>

          {/* CEFR Poster Image */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-lg max-w-3xl mx-auto">
            <img
              src="https://static.readdy.ai/image/80fafa544acf55a90d716b5bdc1b1a54/760d8c3045660c146024442870d3627b.png"
              alt="CEFR Framework Chart - English for Adults Programme"
              className="w-full h-auto object-cover object-top"
            />
          </div>

          {/* CEFR Level Cards */}
          <div className="grid md:grid-cols-3 gap-5">
            {cefrLevels.map((item, index) => (
              <div
                key={index}
                className="bg-[#F5F1E8] rounded-xl p-6 hover:shadow-lg transition-all duration-300 border-l-4"
                style={{ borderLeftColor: index < 2 ? '#FFE66D' : index < 4 ? '#2d5a3f' : '#153223' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span
                    className="text-white font-bold px-3 py-1.5 rounded-lg text-sm"
                    style={{ backgroundColor: index < 2 ? '#d4a017' : index < 4 ? '#2d5a3f' : '#153223' }}
                  >
                    {item.level}
                  </span>
                  <span className="text-sm font-semibold text-gray-600">{item.desc}</span>
                </div>
                <p className="text-[#153223] font-bold text-base">{item.book}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Skills Development */}
      <section className="py-16 bg-[#153223] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="bg-[#FFE66D] text-[#153223] font-bold px-4 py-1.5 rounded-full text-sm inline-block mb-4">
              Career Advantage
            </span>
            <h2 className="font-['Yeseva One'] text-4xl mb-4">
              Useful for Career Skills Development
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Beyond everyday English — our programme equips you with the communication skills that employers value most
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {careerSkills.map((skill, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/15 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFE66D] rounded-full mb-4">
                  <i className={`${skill.icon} text-xl text-[#153223]`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#FFE66D] mb-2">
                  {skill.title}
                </h3>
                <p className="text-gray-300 leading-relaxed text-sm">
                  {skill.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Is It For */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-['Yeseva One'] text-4xl text-[#153223] text-center mb-12">
            Who Is This Programme For?
          </h2>

          <div className="grid md:grid-cols-2 gap-5">
            {whoIsItFor.map((item, index) => (
              <div
                key={index}
                className="flex items-start gap-4 bg-[#F5F1E8] rounded-xl p-5 hover:shadow-md transition-all duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#153223] rounded-full flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-lg text-[#FFE66D]"></i>
                </div>
                <p className="text-gray-800 font-medium leading-relaxed pt-1.5">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Class Format */}
      <section className="py-16 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-['Yeseva One'] text-4xl text-[#153223] text-center mb-12">
            Class Format
          </h2>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFE66D] rounded-full mx-auto mb-4">
                <i className="ri-group-line text-3xl text-[#153223]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#153223] mb-2">Small Group</h3>
              <p className="text-gray-700">Interactive group classes with personalised attention — maximum 10 students per class</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFE66D] rounded-full mx-auto mb-4">
                <i className="ri-time-line text-3xl text-[#153223]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#153223] mb-2">Flexible Schedule</h3>
              <p className="text-gray-700">Classes designed to fit around your work schedule — weekday evenings</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFE66D] rounded-full mx-auto mb-4">
                <i className="ri-computer-line text-3xl text-[#153223]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#153223] mb-2">Online Class</h3>
              <p className="text-gray-700">Join live interactive lessons from anywhere — all you need is a device and internet connection</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFE66D] rounded-full mx-auto mb-4">
                <i className="ri-cup-line text-3xl text-[#153223]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#153223] mb-2">Coffee & Tea Social</h3>
              <p className="text-gray-700">Casual social sessions for adults to practise English over coffee and tea in a relaxed, friendly setting</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFE66D] rounded-full mx-auto mb-4">
                <i className="ri-calendar-check-line text-3xl text-[#153223]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#153223] mb-2">Enrol Anytime</h3>
              <p className="text-gray-700">Class enrolment is available all year round — start your learning journey whenever you are ready.&nbsp;</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#D4C5A9]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Yeseva One'] text-4xl text-[#153223] mb-6">
            Ready to Improve Your English?
          </h2>
          <p className="text-xl text-[#3a3a3a] mb-8">
            Take the first step — register for a free online level test to find the right starting point for you
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="https://wa.link/zxuf0o"
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold px-8 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer flex items-center justify-center gap-2 text-lg"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              Enquire via WhatsApp
            </a>
            <Link
              to="/"
              onClick={handleScrollToTop}
              className="whitespace-nowrap bg-white hover:bg-gray-100 text-[#153223] font-bold px-8 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer flex items-center justify-center gap-2 text-lg"
            >
              <i className="ri-home-line text-xl"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
